import { useContext, useState } from "react";
import { RequestLoader, ToastContext } from "../../../../hooks/context";
import {
  useDownloadStatementMutation,
  useDownloadDebtorStatementMutation,
  useDownloadLedgerAccountStatementMutation,
} from "../../../../hooks/api/mutations/useInventory";
import { cleanObject, getRandomIntInclusive } from "../../../../helpers";
import {
  useGetBankList,
  useGetLedgerAccountList,
} from "../../../../hooks/api/queries";

const useDownloadStatement = ({ closeWidget, type, customer }) => {
  const triggerToast = useContext(ToastContext);
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const [statementFormat, setStatementFormat] = useState("PDF");
  const [startDate, setStartDate] = useState("");
  const [selectedBank, setSelectedBank] = useState();
  const [endDate, setEndDate] = useState("");
  const [selectedStore, setSelectedStore] = useState();
  const [walletId, setWalletId] = useState();
  const [rebatesDetails, setRebatesDetails] = useState();

  const downloadStatement = useDownloadStatementMutation();
  const downloadDebtorStatement = useDownloadDebtorStatementMutation();
  const downloadLedgerAccountStatement =
    useDownloadLedgerAccountStatementMutation();

  const { isLoading: isLoadingLedgerAccountsList, data: ledgerAccountsList } =
    useGetLedgerAccountList({
      enabled: type === "cash",
    });

  const {
    isLoading: isLoadingBankAccountsList,
    data: bankAccount,
    refetch: handleFetchBankAccountsList,
  } = useGetBankList();

  const statementHandlers = {
    debtor: {
      mutate: downloadDebtorStatement,
      buildPayload: (values) =>
        cleanObject({
          customer_id: customer?.id,
          download_format: statementFormat.toLowerCase(),
          start_date: startDate,
          end_date: endDate,
        }),
    },
    ledger: {
      mutate: downloadLedgerAccountStatement,
      buildPayload: (values) =>
        cleanObject({
          ledger_account_id: rebatesDetails,
          download_format: statementFormat.toLowerCase(),
          start_date: startDate,
          end_date: endDate,
        }),
    },
    bank: {
      mutate: downloadLedgerAccountStatement,
      buildPayload: (values) =>
        cleanObject({
          ledger_account_id: selectedBank,
          download_format: statementFormat.toLowerCase(),
          start_date: startDate,
          end_date: endDate,
        }),
    },
    cash: {
      mutate: downloadLedgerAccountStatement,
      buildPayload: (values) => {
        const ledgerId = ledgerAccountsList?.data?.find(
          (d) => d?.store?.id === selectedStore,
        )?.id;

        return cleanObject({
          ledger_account_id: ledgerId,
          download_format: statementFormat.toLowerCase(),
          start_date: startDate,
          end_date: endDate,
        });
      },
    },
    store: {
      mutate: downloadStatement,
      buildPayload: (values) =>
        cleanObject({
          store_wallet_id: walletId,
          type: statementFormat.toLowerCase(),
          start_date: startDate,
          end_date: endDate,
        }),
    },
  };

  const handleDownloadStatement = (values) => {
    const handler = statementHandlers[type];
    if (!handler) return;

    setRequestLoaderProgress(getRandomIntInclusive(10, 50));

    const payload = handler.buildPayload(values);

    handler.mutate.mutate(payload, {
      onSuccess: (res) => {
        if (
          type === "debtor" ||
          type === "ledger" ||
          type === "cash" ||
          type === "bank"
        ) {
          window.open(res?.data?.data);
        } else {
          triggerToast(
            "Statement of account downloaded successfully",
            "success",
          );
        }
        closeWidget();
      },
      onSettled: () => setRequestLoaderProgress(100),
    });
  };

  return {
    handleDownloadStatement,
    downloadStatement,
    downloadDebtorStatement,
    downloadLedgerAccountStatement,
    isLoadingLedgerAccountsList,
    ledgerAccountsList,
    statementFormat,
    setStatementFormat,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    walletId,
    setWalletId,
    selectedStore,
    setSelectedStore,
    isLoadingBankAccountsList,
    bankAccount,
    handleFetchBankAccountsList,
    selectedBank,
    setSelectedBank,
    rebatesDetails,
    setRebatesDetails,
  };
};

export default useDownloadStatement;
